import { useMutation, useQuery } from "react-query";
import {
  addProduct,
  createOrganization,
  deleteOrganization,
  deleteProduct,
  deleteMembership,
  deleteUserInvitation,
  getOrganization,
  getOrganizationFields,
  getOrganizationInvitations,
  getOrganizations,
  getOrganizationUsers,
  getProducts,
  postInviteUser,
  updateOrganizationProductFields,
  updateOrganizationInfo,
  getCreditorCatalog,
  updateMembership,
} from "../services/organizations";
import { queryClient } from "../App";
import {
  CreateOrganizationInterface,
  Member,
  MembershipLevelType,
  OrganizationInterface,
  OrganizationPatchInterface,
  ProductType,
} from "../types";
import { uniqBy } from "lodash";
import GlobalSharedStateClass from "../util/globalSharedStateClass";
import { ORGANIZATION_MEMBERSHIPS } from "../util/constants";

const onSuccess = (response: any) => {
  if (!response.data) return;
  return queryClient.setQueryData(
    "organizations",
    (value: { data: OrganizationInterface; meta: { count: number } } | any) => {
      return {
        data: uniqBy([...value.data, ...response.data], "id"),
        meta: { count: response.meta.count || value.meta.count },
      };
    }
  );
};

const getOrganizationFn = async () => {
  const Cache = new GlobalSharedStateClass();
  const result:
    | { data: OrganizationInterface[]; meta: { count: number } }
    | any = await getOrganizations({});
  const data:
    | { data: OrganizationInterface[]; meta: { count: number } }
    | undefined = queryClient.getQueryData("organizations");
  let selectedOrganizations = Cache.getCacheSelectedOrganizations() || [];

  if (data && result?.data) {
    return {
      data: uniqBy(
        [...data.data, ...result.data, ...selectedOrganizations],
        "id"
      ),
      meta: data.meta,
    };
  }

  return result;
};

export const useGetOrganizations = (open?: boolean) => {
  const optionalParams = open !== undefined ? { enabled: open } : {};
  return useQuery("organizations", getOrganizationFn, {
    ...optionalParams,
    refetchOnMount: false,
  });
};

const getOrganizationsByQuery = ({
  offset,
  search,
  count,
}: {
  offset?: number;
  search?: string;
  count?: number;
}): Promise<unknown> => getOrganizations({ offset, search, count });

export const useLazyOrganizationsMutation = () => {
  // @ts-ignore
  return useMutation(getOrganizationsByQuery, {
    onSuccess,
    // retry: true
  });
};

export const useGetOrganization = (id: number) => {
  return useQuery(["organization", id], () => getOrganization(id), {
    cacheTime: 60 * 5000,
    refetchOnMount: false,
    enabled: !!id,
  });
};

export const useGetOrganizationFields = (id: number) => {
  return useQuery(
    ["organization-fields", id],
    () => getOrganizationFields(id),
    {
      cacheTime: 60 * 5000,
      refetchOnMount: false,
    }
  );
};

export const useGetAllProducts = () => {
  return useQuery(["products"], () => getProducts(), {
    refetchOnMount: false,
  });
};

export const useGetOrganizationUsers = (id: number) => {
  return useQuery(
    [ORGANIZATION_MEMBERSHIPS, id],
    () => getOrganizationUsers(id),
    {
      cacheTime: 60 * 5000,
      refetchOnMount: false,
    }
  );
};

export const useGetOrganizationInvitations = (id: number) => {
  return useQuery(
    ["organization-invitations", id],
    () => getOrganizationInvitations(id),
    {
      cacheTime: 60 * 5000,
      refetchOnMount: false,
    }
  );
};

const onInviteUserSuccess = (
  response: Member | undefined,
  variables: { id: number }
) => {
  if (!response) return;
  queryClient.setQueryData(
    ["organization-invitations", variables.id],
    (value: Member[] | undefined = []) => {
      return [...value, response];
    }
  );
};

export const useInviteUserMutation = () => {
  return useMutation(
    ({
      id,
      email,
      level,
    }: {
      id: number;
      email: string;
      level: string;
    }): Promise<unknown | any> => postInviteUser({ id, email, level }),
    {
      onSuccess: onInviteUserSuccess,
      retry: false,
    }
  );
};

const onDeleteInvitationSuccess = (
  response: any,
  variables: { invitationId: number; organizationId: number }
) => {
  queryClient.setQueryData(
    ["organization-invitations", variables.organizationId],
    (value: Member[] | undefined = []) => {
      return value.filter((item: Member) => item.id !== variables.invitationId);
    }
  );
};

export const useDeleteInvitationMutation = () => {
  return useMutation(
    ({
      invitationId,
    }: {
      invitationId: number;
      organizationId: number;
    }): Promise<unknown | any> => deleteUserInvitation(invitationId),
    {
      onSuccess: onDeleteInvitationSuccess,
      retry: false,
    }
  );
};

const onCreateOrganizationSuccess = (response: OrganizationInterface) => {
  queryClient.setQueryData(["organizations"], (value: any) => {
    return {
      data: [...value.data, response],
      meta: { count: (value.meta.count as number) + 1 },
    };
  });
};

export const useCreateOrganizationMutation = () => {
  return useMutation(
    (values: CreateOrganizationInterface): Promise<unknown | any> => {
      return createOrganization(values);
    },
    {
      onSuccess: onCreateOrganizationSuccess,
      retry: false,
    }
  );
};

const onDeleteOrganizationSuccess = (
  response: any,
  variables: { id: number }
) => {
  queryClient.setQueryData(
    ["organizations"],
    (value: { data: Member[]; meta: { count: number } } | any) => {
      return {
        data: value?.data?.filter((item: Member) => item.id !== variables.id),
        meta: { count: (value?.meta?.count as number) - 1 },
      };
    }
  );
};

export const useDeleteOrganizationMutation = () => {
  return useMutation(
    ({ id }: { id: number }): Promise<unknown | any> => deleteOrganization(id),
    {
      onSuccess: onDeleteOrganizationSuccess,
      retry: false,
    }
  );
};

const onAddProductSuccess = (
  response: any,
  variables: { organizationId: number; productId: number }
) => {
  const products: ProductType[] = queryClient.getQueryData(["products"]) || [];
  const selectedProduct = products.find(
    (item: ProductType) => item.id === variables.productId
  );

  queryClient.setQueryData(
    ["organization", variables.organizationId],
    (value: OrganizationInterface | any) => {
      return {
        ...(value as object),
        products: [...value?.products, selectedProduct],
      };
    }
  );
};

export const useAddProductMutation = () => {
  return useMutation(
    ({
      organizationId,
      productId,
    }: {
      organizationId: number;
      productId: number;
    }): Promise<unknown | any> => {
      return addProduct({ organizationId, productId });
    },
    {
      onSuccess: onAddProductSuccess,
      retry: false,
    }
  );
};

const onDeleteProductSuccess = (
  response: any,
  variables: { organizationId: number; productId: number }
) => {
  queryClient.setQueryData(
    ["organization", variables.organizationId],
    (value: OrganizationInterface | any) => {
      return {
        ...(value as object),
        products: value.products.filter(
          (item: ProductType) => item.id !== variables.productId
        ),
      };
    }
  );
};

export const useDeleteProductMutation = () => {
  return useMutation(
    ({
      organizationId,
      productId,
    }: {
      organizationId: number;
      productId: number;
    }): Promise<unknown | any> => {
      return deleteProduct({ organizationId, productId });
    },
    {
      onSuccess: onDeleteProductSuccess,
      retry: false,
    }
  );
};

export const useUpdateOrganizationProductFields = () => {
  return useMutation(
    ({
      organizationId,
      fields,
    }: {
      organizationId: number;
      fields: { id: number; validation?: boolean }[];
    }): Promise<unknown | any> => {
      return updateOrganizationProductFields({ organizationId, fields });
    },
    {
      retry: false,
    }
  );
};

const onDeleteMembershipSuccess = (
  response: any,
  variables: { organizationId: number; memberId: number }
) => {
  queryClient.setQueryData(
    [ORGANIZATION_MEMBERSHIPS, variables.organizationId],
    (value: Member[] | any) => {
      return value.filter((item: Member) => item.id !== variables.memberId);
    }
  );
};

export const useDeleteMembershipMutation = () => {
  return useMutation(
    ({
      memberId,
    }: {
      organizationId: number;
      memberId: number;
    }): Promise<unknown | any> => {
      return deleteMembership(memberId);
    },
    {
      onSuccess: onDeleteMembershipSuccess,
      retry: false,
    }
  );
};

export const useUpdateOrganizationInfo = () => {
  return useMutation(
    ({
      organizationId,
      organizationData,
    }: {
      organizationId: number;
      organizationData: OrganizationPatchInterface;
    }): Promise<unknown | any> => {
      return updateOrganizationInfo({ organizationId, organizationData });
    },
    {
      // onSuccess: onDeleteMembershipSuccess,
      retry: false,
    }
  );
};

export const useGetCreditorCatalog = (id: number) => {
  return useQuery(["creditor-catalog", id], () => getCreditorCatalog(id), {
    enabled: !!id,
    cacheTime: 60 * 5000,
    refetchOnMount: false,
  });
};

const onUpdateMembershipSuccess = (
  response: Member,
  variables: { organizationId: number; membershipId: number }
) => {
  queryClient.setQueryData(
    [ORGANIZATION_MEMBERSHIPS, variables.organizationId],
    (values: Member[] | any) => {
      return values.map((item: Member) => {
        if (item.id === response.id) {
          return response;
        }
        return item;
      });
    }
  );
};

export const useUpdateMembershipMutation = () => {
  return useMutation(
    ({
      membershipId,
      data,
    }: {
      organizationId: number;
      membershipId: number;
      data: { label: string; level: MembershipLevelType };
    }): Promise<unknown | any> => {
      return updateMembership(membershipId, data);
    },
    {
      onSuccess: onUpdateMembershipSuccess,
      retry: false,
    }
  );
};
